import React, { useState } from 'react';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { useDispatch, useSelector } from 'react-redux';
import { ComponentDataTable, DefaultModal, SimpleCenteredModal } from '../../../components';
import Columns from './Columns';
import MassiveActions from './MassiveActions';
import { sendAlert } from '../../../actions/utils';
import {
  deleteSettlementRequest,
  exportSettlementsRequest,
  generateSettlementDocument,
  indexSettlementsRequest,
  massiveDestroySettlementsRequest
} from '../../../requests/payrollSettlements';
import SettlementDocumentForm from '../Form/SettlementDocumentForm';
import DocumentPreview from '../../DocumentPreview/DocumentPreview';
import { downloadFile } from '../../../services/utils';
import { debounceIndexDocumentsRequest } from '../../../requests/documents';
import styles from './styles.module.css';
import DocumentSelector from './DocumentSelector';
import Filters from '../Filters';

const defaultModalState = {
  show: false,
  title: '',
  body: '',
  handleAction: () => {},
  disabled: false
};

const TableIndividual = () => {
  const [onRequest, setOnRequest] = useState(true);
  const [settlements, setSettlements] = useState([]);
  const [amount, setAmount] = useState(10);
  const [moreData, setMoreData] = useState(false);
  const { currentCompany } = useSelector(state => state.auth);
  const [clearSelectedRows, setClearSelectedRows] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [defaultModal, setDefaultModal] = useState(defaultModalState);
  const [modalSize, setModalSize] = useState('lg');
  const [modalBody, setModalBody] = useState('');
  const [modalShow, setModalShow] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const history = useHistory();
  const dispatch = useDispatch();

  const handleSelectedRows = item => {
    setSelectedRows(item.selectedRows);
  };

  const handleIndexRequest = params => {
    if (!currentCompany.id) return;
    setOnRequest(true);
    indexSettlementsRequest({
      dispatch,
      params: {
        filter_by_company: currentCompany.id,
        filter_no_group: true,
        ...params
      },
      successCallback: ({ data }) => {
        setSettlements(data.data);
        setAmount(data.metaData?.amount);
      },
      callback: () => {
        setOnRequest(false);
      }
    });
  };

  const handleDelete = id => {
    setDefaultModal(oldState => ({ ...oldState, disabled: true }));
    deleteSettlementRequest(id, {
      dispatch,
      successCallback: () => {
        dispatch(sendAlert({ kind: 'success', message: 'Finiquito eliminado con éxito' }));
        setDefaultModal(oldState => ({ ...oldState, show: false }));
        setMoreData(!moreData);
      },
      failureCallback: () => {},
      callback: () => {
        setDefaultModal(oldState => ({ ...oldState, disabled: false }));
      }
    });
  };

  const handleDownloadDocument = ids => {
    if (ids.length === 0) {
      dispatch(sendAlert({ kind: 'warning', message: 'El finiquito seleccionado no tiene documentos asociados' }));
      setModalShow(false);
      return;
    }
    debounceIndexDocumentsRequest({
      dispatch,
      params: { filter_ids: ids },
      format: '.zip',
      responseType: 'blob',
      successCallback: downloadFile,
      callback: () => {
        setOnRequest(false);
        setModalShow(false);
        setClearSelectedRows(!clearSelectedRows);
      }
    });
  };

  const handleShowDocument = (settlement, file) => {
    return <DocumentPreview documentId={settlement[file]?.document?.id} employeeId={settlement.employee.id} />;
  };

  const handleSettlementDocument = (values, settlementId) => {
    generateSettlementDocument(settlementId, {
      dispatch,
      params: {
        template_id: values.templatePdf.templateId,
        document_type: values.templatePdf.documentType,
        extra_keys: values.templatePdf.extraKeys
      },
      successCallback: data => {
        dispatch(sendAlert({ kind: 'success', message: 'Documento generado con éxito' }));
        const body = handleShowDocument(data.data, data.data.file_column);
        setModalTitle('');
        setModalBody(body);
        setModalSize('xl');
      },
      callback: () => {
        setMoreData(oldVal => !oldVal);
      }
    });
  };

  const handleSelectedDocument = (file, settlement) => {
    const body = handleShowDocument(settlement, file?.documentType);
    setModalBody(body);
    setModalSize('xl');
    setModalTitle('');
  };

  const handleDocumentSelector = item => {
    setModalShow(false);
    const allowedKeys = [
      { value: 'summary_file', label: 'Resumen Finiquito' },
      { value: 'termination_letter_file', label: 'Carta Finiquito' },
      { value: 'settlement_file', label: 'Finiquito' }
    ];
    const showFields = allowedKeys.filter(key => item[key.value]?.document?.id);
    setModalBody(<DocumentSelector showFields={showFields} settlement={item} formRequest={handleSelectedDocument} />);
    setModalTitle('Seleccion de Documento');
    setModalSize('lg');
    setModalShow(true);
  };

  const handleActions = (item, action) => {
    switch (action) {
      case 'edit':
        localStorage.removeItem('settlementForm');
        history.push(`/settlement_simulator/${item.id}/edit`);
        break;
      case 'show_document': {
        handleDocumentSelector(item);
        break;
      }
      case 'generate_document': {
        setModalTitle('Generar Documento');
        setModalBody(<SettlementDocumentForm settlementId={item.id} formRequest={handleSettlementDocument} />);
        setModalSize('lg');
        setModalShow(true);
        break;
      }
      case 'show_calculation': {
        const body = handleShowDocument(item, 'summary_file');
        setModalTitle('Cálculo de finiquito de trabajador');
        setModalBody(body);
        setModalSize('xl');
        setModalShow(true);
        break;
      }
      case 'download': {
        const allowedKeys = ['summary_file', 'termination_letter_file', 'settlement_file'];
        const documentIds = allowedKeys.filter(key => item[key]?.document?.id).map(key => item[key]?.document?.id);
        handleDownloadDocument(documentIds);
        break;
      }
      case 'show_letter': {
        const body = handleShowDocument(item, 'termination_letter_file');
        setModalTitle('Carta de término');
        setModalSize('xl');
        setModalBody(body);
        setModalShow(true);
        break;
      }
      case 'show_settlement': {
        history.push(`/settlement_simulator/${item.id}/view_summary`);
        break;
      }
      case 'destroy': {
        const isSimulation = item.simulation;
        const modalText = isSimulation
          ? '¿Está seguro que desea eliminar la simulación de finiquito?'
          : '¿Está seguro que desea eliminar el finiquito?';
        setDefaultModal({
          show: true,
          title: 'Eliminar Finiquito',
          body: modalText,
          handleAction: () => handleDelete(item.id)
        });
        break;
      }
      default:
        break;
    }
  };

  const handleMassiveDestroy = ids => {
    setDefaultModal(oldState => ({ ...oldState, disabled: true }));
    massiveDestroySettlementsRequest({
      dispatch,
      params: {
        filter_by_company: currentCompany.id,
        ...ids
      },
      successCallback: () => {
        dispatch(sendAlert({ kind: 'success', message: 'Finiquitos eliminados con éxito' }));
        setDefaultModal(oldState => ({ ...oldState, show: false }));
        setMoreData(!moreData);
      },
      callback: () => {
        setDefaultModal(oldState => ({ ...oldState, disabled: false }));
      }
    });
  };

  const handleMassiveActions = action => {
    const selectedIds =
      selectedRows.map(row => row.id).length > 0 ? { filter_id: selectedRows.map(row => row.id) } : {};
    switch (action) {
      case 'download_excel':
        exportSettlementsRequest({
          dispatch,
          params: {
            filter_by_company: currentCompany.id,
            ...selectedIds
          },
          format: '.xlsx',
          successCallback: data => {
            downloadFile(data, 'finiquitos.xlsx');
          },
          callback: () => {}
        });
        break;
      case 'destroy': {
        const modalText = !Object.keys(selectedIds).length
          ? '¿Está seguro que desea eliminar todos los finiquitos?'
          : '¿Está seguro que desea eliminar los finiquitos seleccionados?';
        setDefaultModal({
          show: true,
          title: 'Eliminar Finiquito',
          body: modalText,
          handleAction: () => handleMassiveDestroy(selectedIds)
        });
        break;
      }
      default:
        break;
    }
  };

  return (
    <>
      <Filters />
      {currentCompany.id && (
        <div className="data-table-yellow">
          <ComponentDataTable
            onRequest={onRequest}
            columns={Columns(handleActions)}
            customClassModule={styles['fix-padding-bottom']}
            data={settlements}
            totalRows={amount}
            moreData={moreData}
            resourceRequest={handleIndexRequest}
            preName="TableIndividual"
            withMassActions
            massActions={<MassiveActions disabled={false} handleClick={handleMassiveActions} />}
            selectableRows
            clearSelectedRows={clearSelectedRows}
            onSelectedRowsChange={handleSelectedRows}
            withDateSelect
            withStartDate
            withEndDate
          />
        </div>
      )}
      <DefaultModal
        show={defaultModal.show}
        handleClose={() => setDefaultModal(oldState => ({ ...oldState, show: false }))}
        title={defaultModal.title}
        body={defaultModal.body}
        handleConfirm={defaultModal.handleAction}
        withClose
        withConfirm
        disabled={defaultModal.disabled}
      />
      <SimpleCenteredModal
        title={modalTitle}
        body={modalBody}
        show={modalShow}
        onHide={() => setModalShow(false)}
        bodyClass="mx-3 mb-3"
        size={modalSize}
      />
    </>
  );
};

export default TableIndividual;
